import { useState, useEffect } from 'react';
import { profileAPI } from '../services/api';
import './MyProfile.css';

export default function MyProfile({ onClose }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Edit state
  const [bio, setBio] = useState('');
  const [profilePhoto, setProfilePhoto] = useState('');
  const [backgroundPhoto, setBackgroundPhoto] = useState('');
  const [friendsListPrivate, setFriendsListPrivate] = useState(false);

  // Friend requests
  const [friendRequests, setFriendRequests] = useState([]);
  const [showFriendRequests, setShowFriendRequests] = useState(false);

  useEffect(() => {
    loadProfile();
    loadFriendRequests();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await profileAPI.getMe();
      const user = response.data.user;
      setProfile(user);
      setBio(user.profile?.bio || '');
      setProfilePhoto(user.profile?.profilePhoto || '');
      setBackgroundPhoto(user.profile?.backgroundPhoto || '');
      setFriendsListPrivate(user.friendsListPrivate || false);
    } catch (err) {
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const loadFriendRequests = async () => {
    try {
      const response = await profileAPI.getFriendRequests();
      setFriendRequests(response.data.requests);
    } catch (err) {
      console.error('Failed to load friend requests', err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await profileAPI.updateMe({
        bio,
        profilePhoto,
        backgroundPhoto,
        friendsListPrivate
      });
      setSuccess('Profile updated!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleFriendRequest = async (requestId, action) => {
    try {
      await profileAPI.respondToFriendRequest(requestId, action);
      setFriendRequests(prev => prev.filter(r => r._id !== requestId));
      if (action === 'accept') {
        loadProfile(); // Reload to update friends list
      }
    } catch (err) {
      setError('Failed to respond to friend request');
    }
  };

  const handleRemoveFriend = async (friendId) => {
    if (!confirm('Are you sure you want to remove this friend?')) return;

    try {
      await profileAPI.removeFriend(friendId);
      setProfile(prev => ({
        ...prev,
        friends: prev.friends.filter(f => f._id !== friendId)
      }));
    } catch (err) {
      setError('Failed to remove friend');
    }
  };

  if (loading) {
    return (
      <div className="my-profile-overlay">
        <div className="my-profile-panel">
          <div className="profile-loading">Loading profile...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="my-profile-overlay" onClick={onClose}>
      <div className="my-profile-panel" onClick={e => e.stopPropagation()}>
        <div className="profile-header">
          <h2>My Profile</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        {error && <div className="profile-error">{error}</div>}
        {success && <div className="profile-success">{success}</div>}

        {/* Profile Preview */}
        <div
          className="profile-preview"
          style={{ backgroundImage: backgroundPhoto ? `url(${backgroundPhoto})` : undefined }}
        >
          <div className="profile-photo-container">
            {profilePhoto ? (
              <img src={profilePhoto} alt="Profile" className="profile-photo" />
            ) : (
              <div className="profile-photo-placeholder">
                {profile?.username?.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className="profile-username">{profile?.username}</div>
        </div>

        {/* Edit Form */}
        <div className="profile-form">
          <div className="form-group">
            <label>Profile Photo URL</label>
            <input
              type="url"
              value={profilePhoto}
              onChange={e => setProfilePhoto(e.target.value)}
              placeholder="https://example.com/photo.jpg"
            />
          </div>

          <div className="form-group">
            <label>Background Photo URL</label>
            <input
              type="url"
              value={backgroundPhoto}
              onChange={e => setBackgroundPhoto(e.target.value)}
              placeholder="https://example.com/background.jpg"
            />
          </div>

          <div className="form-group">
            <label>Bio ({bio.length}/200)</label>
            <textarea
              value={bio}
              onChange={e => setBio(e.target.value.slice(0, 200))}
              placeholder="Tell others about yourself..."
              rows={3}
              maxLength={200}
            />
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={friendsListPrivate}
                onChange={e => setFriendsListPrivate(e.target.checked)}
              />
              <span>Make friends list private</span>
            </label>
          </div>

          <button
            className="btn btn-primary save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {/* Friend Requests */}
        {friendRequests.length > 0 && (
          <div className="friend-requests-section">
            <button
              className="section-toggle"
              onClick={() => setShowFriendRequests(!showFriendRequests)}
            >
              Friend Requests ({friendRequests.length})
              <span className={`toggle-arrow ${showFriendRequests ? 'open' : ''}`}>▼</span>
            </button>
            {showFriendRequests && (
              <div className="friend-requests-list">
                {friendRequests.map(request => (
                  <div key={request._id} className="friend-request-item">
                    <div className="request-user">
                      {request.from.profile?.profilePhoto ? (
                        <img src={request.from.profile.profilePhoto} alt="" className="request-avatar" />
                      ) : (
                        <div className="request-avatar-placeholder">
                          {request.from.username.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span>{request.from.username}</span>
                    </div>
                    <div className="request-actions">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleFriendRequest(request._id, 'accept')}
                      >
                        Accept
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleFriendRequest(request._id, 'reject')}
                      >
                        Decline
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Friends List */}
        <div className="friends-section">
          <h3>Friends ({profile?.friends?.length || 0})</h3>
          {profile?.friends?.length > 0 ? (
            <div className="friends-list">
              {profile.friends.map(friend => (
                <div key={friend._id} className="friend-item">
                  <div className="friend-info">
                    {friend.profile?.profilePhoto ? (
                      <img src={friend.profile.profilePhoto} alt="" className="friend-avatar" />
                    ) : (
                      <div className="friend-avatar-placeholder">
                        {friend.username.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="friend-username">{friend.username}</span>
                    <span className={`online-status ${friend.isOnline ? 'online' : 'offline'}`}>
                      {friend.isOnline ? 'Online' : 'Offline'}
                    </span>
                  </div>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleRemoveFriend(friend._id)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-friends">No friends yet. Add friends by clicking on usernames in game!</p>
          )}
        </div>
      </div>
    </div>
  );
}
